import { Divider, MenuItem, Stack, TextField, Typography } from "@mui/material";
import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useUpdateMedicationMutation } from "src/api/services/medicationsApi";
import { Medication, MedicationForm, MedicationFormEnums } from "src/api/types";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import DrawerFieldRow from "src/shared/DrawerFieldRow";
import DrawerForm from "src/shared/DrawerForm";

interface Props {
  open: boolean;
  onClose: () => void;
  medication: Medication;
}

const UpdateMedicationDrawer = ({ medication, onClose, open }: Props) => {
  const [updateMedication] = useUpdateMedicationMutation();
  const [loading, setLoading] = useState<boolean>(false);
  const [name, setName] = useState<string>(medication.name);
  const [displayName, setDisplayName] = useState<string | null>(
    medication.display_name ?? null,
  );
  const [dosage, setDosage] = useState<string>(medication.dosage);
  const [strength, setStrength] = useState<string>(medication.strength);
  const [form, setForm] = useState<MedicationForm>(medication.form);
  const [dispenseUnit, setDispenseUnit] = useState<string>(
    medication.dispense_unit,
  );
  const [dispenseQuantity, setDispenseQuantity] = useState<number>(
    medication.dispense_quantity,
  );
  const [drugId, setDrugId] = useState<string>(medication.drug_id);
  const [ndc, setNdc] = useState<string | null>(medication.ndc ?? null);
  const errLog = useSentryLogger("error");

  const handleMedicationName = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setName(e.target.value);
    },
    [],
  );

  const handleDisplayName = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.target.value.length === 0) {
        setDisplayName(null);
        return;
      }

      setDisplayName(e.target.value);
    },
    [],
  );

  const handleDosage = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setDosage(e.target.value);
    },
    [],
  );

  const handleStrength = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setStrength(e.target.value);
    },
    [],
  );

  const handleDispenseUnit = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setDispenseUnit(e.target.value);
    },
    [],
  );

  const handleDispenseQuantity = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setDispenseQuantity(Number(e.target.value));
    },
    [],
  );

  const handleForm = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm(e.target.value as MedicationForm);
    },
    [],
  );

  const handleDrugId = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setDrugId(e.target.value);
    },
    [],
  );

  const handleNdc = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.target.value.length === 0) {
        setNdc(null);
        return;
      }
      setNdc(e.target.value);
    },
    [],
  );

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);

      try {
        await updateMedication({
          id: medication.id,
          body: {
            dispense_quantity: dispenseQuantity,
            dispense_unit: dispenseUnit,
            display_name: displayName,
            name,
            dosage,
            form,
            strength,
            drug_id: drugId,
            ndc,
          },
        });
      } catch (err) {
        errLog("Error update medication", err);
      } finally {
        setLoading(false);
        onClose();
      }
    },
    [
      dispenseQuantity,
      dispenseUnit,
      displayName,
      dosage,
      drugId,
      errLog,
      form,
      medication.id,
      name,
      ndc,
      onClose,
      strength,
      updateMedication,
    ],
  );

  useEffect(() => {
    setName(medication.name);
    setDispenseQuantity(medication.dispense_quantity);
    setDispenseUnit(medication.dispense_unit);
    setDisplayName(medication.display_name ?? "");
    setDosage(medication.dosage);
    setStrength(medication.strength);
    setForm(medication.form);
    setNdc(medication.ndc ?? "");
    setDrugId(medication.drug_id);
  }, [
    medication.dispense_quantity,
    medication.dispense_unit,
    medication.display_name,
    medication.dosage,
    medication.form,
    medication.name,
    medication.strength,
    medication.ndc,
    medication.drug_id,
  ]);

  return (
    <DrawerForm
      label="Update Medication"
      onClose={onClose}
      open={open}
      handleSubmit={handleSubmit}
      loading={loading}
    >
      <Stack gap={2} component="form">
        <DrawerFieldRow label="id" type="input">
          <TextField disabled value={medication.id} size="small" />
        </DrawerFieldRow>
        <DrawerFieldRow label="name" type="input">
          <TextField
            value={name}
            onChange={handleMedicationName}
            size="small"
            multiline
          />
        </DrawerFieldRow>
        <DrawerFieldRow label="dosage" type="input">
          <TextField value={dosage} onChange={handleDosage} size="small" />
        </DrawerFieldRow>
        <DrawerFieldRow label="strength" type="input">
          <TextField value={strength} onChange={handleStrength} size="small" />
        </DrawerFieldRow>
        <DrawerFieldRow label="form" type="input">
          <TextField value={form} onChange={handleForm} size="small" select>
            {MedicationFormEnums.map((form) => (
              <MenuItem key={form} value={form}>
                {form}
              </MenuItem>
            ))}
          </TextField>
        </DrawerFieldRow>
        <DrawerFieldRow label="dispense unit" type="input">
          <TextField
            value={dispenseUnit}
            onChange={handleDispenseUnit}
            size="small"
          />
        </DrawerFieldRow>
        <DrawerFieldRow label="dispense quantity" type="input">
          <TextField
            value={dispenseQuantity}
            onChange={handleDispenseQuantity}
            type="number"
            size="small"
          />
        </DrawerFieldRow>
        <DrawerFieldRow label="drug id" type="input">
          <TextField
            value={drugId}
            onChange={handleDrugId}
            size="small"
            multiline
          />
        </DrawerFieldRow>
        <Typography variant="subtitle1">Optional fields</Typography>
        <Divider />
        <DrawerFieldRow label="display name" type="input">
          <TextField
            value={displayName}
            onChange={handleDisplayName}
            size="small"
            multiline
          />
        </DrawerFieldRow>
        <DrawerFieldRow label="ndc" type="input">
          <TextField value={ndc} onChange={handleNdc} size="small" />
        </DrawerFieldRow>
      </Stack>
    </DrawerForm>
  );
};

export default UpdateMedicationDrawer;
